import {useMemo, useState} from 'react';

import {Button, SearchBar, TableSkeleton} from '@/components';

import ResolverTable from './ResolverTable';
import {hasConfiguredResolver} from './types';
import UpdateResolverModal from './UpdateResolverModal';
import useResolverList from './useResolverList';

const resolveEmptyStateMessage = (search: string, showAll: boolean) => {
  if (search) {
    return `No currency matches "${search}".`;
  }
  if (showAll) {
    return 'No currencies found.';
  }
  return 'No resolvers configured yet. Use "Show all currencies" to configure one.';
};

/**
 * The Configuration tab of Processors -> Resolvers: which provider resolves an
 * account holder's name per currency, and which processor an ACH account is
 * created with.
 *
 * These are not the corridor processors. BANK_ACCOUNT, MOBILE_MONEY and
 * ACH_BANK_ACCOUNT are node-routed, so their corridor slots are the deposit and
 * withdraw search order. These answer a different question -- which provider
 * looks up an account holder's name, and which processor an ACH account is
 * created with -- and may legitimately differ for the same currency.
 */
const ResolverConfiguration = () => {
  const {
    isLoading,
    providers,
    matrix,
    countryList,
    countryCodeByCurrency,
    currencyList,
    selectedCurrency,
    shouldShowUpdateForm,
    showResolverUpdateForm,
    closeResolverUpdateForm,
    handleUpdateResolvers,
  } = useResolverList();

  // One currency document per supported asset (64 of them), but only a handful
  // name a resolver. Default to those, and keep the rest one click away so a
  // new one can still be configured. Same reasoning as the Corridors screen.
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');

  const configuredCurrencies = useMemo(
    () => (currencyList ?? []).filter(hasConfiguredResolver),
    [currencyList],
  );

  const visibleCurrencies = useMemo(() => {
    const base = showAll ? (currencyList ?? []) : configuredCurrencies;
    const term = search.trim().toLowerCase();
    if (!term) {
      return base;
    }
    return base.filter(
      (c) =>
        c?.code?.toLowerCase().includes(term) ||
        c?.name?.toLowerCase().includes(term),
    );
  }, [showAll, search, currencyList, configuredCurrencies]);

  const hiddenCount = (currencyList?.length ?? 0) - configuredCurrencies.length;

  // Without this an unconfigured screen renders a bare table header, which
  // reads as broken rather than empty. Especially true before the backfill has
  // run anywhere, when nothing is configured at all.
  const emptyStateMessage = resolveEmptyStateMessage(search, showAll);

  // Extracted rather than nested in the loading ternary, which
  // `no-nested-ternary` rejects. Same shape as the Corridors screen.
  const loadedContent = visibleCurrencies.length ? (
    <ResolverTable
      items={visibleCurrencies}
      countryList={countryList}
      countryCodeByCurrency={countryCodeByCurrency}
      matrix={matrix}
      showResolverUpdateForm={showResolverUpdateForm}
    />
  ) : (
    <p className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
      {emptyStateMessage}
    </p>
  );

  return (
    <>
      <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
        The provider that resolves an account holder’s name per currency, and
        the processor an ACH account is created with. These are separate from
        the corridor processors under Processors → Corridors, and may
        legitimately differ.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-3 pb-5">
        <SearchBar
          handleSearch={setSearch}
          handleAutoComplete={setSearch}
          placeholder="Search currency"
          clearFilters={() => setSearch('')}
        />
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          <span>
            {configuredCurrencies.length} configured
            {!showAll && hiddenCount > 0 ? ` \u00b7 ${hiddenCount} hidden` : ''}
          </span>
          {hiddenCount > 0 ? (
            <Button variant="outline" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show configured only' : 'Show all currencies'}
            </Button>
          ) : null}
        </div>
      </div>

      {isLoading ? <TableSkeleton /> : loadedContent}

      {shouldShowUpdateForm && (
        <UpdateResolverModal
          isOpen={shouldShowUpdateForm}
          currency={selectedCurrency}
          providers={providers}
          matrix={matrix}
          onClose={closeResolverUpdateForm}
          onSubmit={(resolvers) =>
            handleUpdateResolvers({
              currency: selectedCurrency as NonNullable<
                typeof selectedCurrency
              >,
              resolvers: resolvers as Parameters<
                typeof handleUpdateResolvers
              >[0]['resolvers'],
            })
          }
        />
      )}
    </>
  );
};

export default ResolverConfiguration;
